import { useMemo, useState } from "react";
import { activeOps, deserializeEditGraph } from "@desk/core";
import { PACK_REGISTRY } from "../content/registry.js";
import { resolveResultCardTheme } from "../game/store-catalog.js";
import type { GameController } from "../shell/hooks/useGameController.js";
import { storyMotifFor } from "../shell/lib/story-motifs.js";
import { ArtifactPreview } from "../shell/ui/ArtifactPreview.js";
import { FlowChrome } from "../shell/ui/FlowChrome.js";
import { FlowEmpty } from "../shell/ui/FlowStates.js";
import { ShareChallengeButton } from "../shell/ui/ShareChallengeButton.js";

function editChoicesSummary(graphJson: string): string {
  try {
    const graph = deserializeEditGraph(graphJson);
    const ops = activeOps(graph);
    const kinds = new Map<string, number>();
    for (const op of ops) {
      kinds.set(op.kind, (kinds.get(op.kind) ?? 0) + 1);
    }
    if (kinds.size === 0) return "Default cut";
    return [...kinds.entries()]
      .map(([k, n]) => `${k}×${n}`)
      .join(" · ");
  } catch {
    return "Edit";
  }
}

export function PortfolioScreen({ game }: { game: GameController }) {
  const { state, dispatch } = game;
  const [activePreviewId, setActivePreviewId] = useState<string | null>(
    state.portfolio[0]?.id ?? null,
  );

  const cards = useMemo(() => state.portfolio, [state.portfolio]);

  return (
    <FlowChrome
      testId="screen-portfolio"
      className="portfolio-screen"
      stage="Portfolio"
      title="Your reels"
      hook={`${state.portfolio.length} of ${state.portfolioSlotLimit} slots.`}
      onBack={() => dispatch({ type: "CLOSE_PORTFOLIO" })}
      backLabel="Close"
      backTestId="close-portfolio"
    >
      {cards.length === 0 ? (
        <FlowEmpty message="Published reels will appear here." testId="portfolio-empty" />
      ) : null}

      <ul className="portfolio-list portfolio-grid" data-testid="portfolio-list">
        {cards.map((p) => {
          const entry = PACK_REGISTRY[p.packId];
          const active = activePreviewId === p.id;
          const motif = storyMotifFor(p.packId);
          const trend = entry?.pack.trends[0];
          return (
            <li
              key={p.id}
              className="portfolio-card"
              data-testid={`portfolio-card-${p.id}`}
              style={{ ["--story-accent" as string]: motif.accent }}
            >
              {entry && (
                <ArtifactPreview
                  packId={p.packId}
                  pack={entry.pack}
                  graphJson={p.graphJson}
                  label={entry.title}
                  scoreText={`Reach ${p.score.reach}`}
                  resultCardTheme={resolveResultCardTheme({
                    purchases: state.purchases,
                    equippedTheme: state.equippedTheme,
                  })}
                  compact
                  active={active}
                  testId={`portfolio-artifact-${p.id}`}
                />
              )}
              <div className="portfolio-card-main">
                <strong>{entry?.title ?? "Reel"}</strong>
                <div className="portfolio-chips">
                  {trend ? (
                    <span className="badge">{trend.label ?? trend.id}</span>
                  ) : null}
                  <span className="badge badge-reach">Reach {p.score.reach}</span>
                  <span className="badge">Cred {p.score.credDelta}</span>
                </div>
                <p className="meta sr-only" data-testid={`portfolio-summary-${p.id}`}>
                  {editChoicesSummary(p.graphJson)}
                </p>
                <div className="cta-row">
                  <button
                    type="button"
                    className="secondary"
                    data-testid={`portfolio-preview-${p.id}`}
                    aria-pressed={active}
                    onClick={() => setActivePreviewId(p.id)}
                  >
                    {active ? "Previewing" : "Show preview"}
                  </button>
                </div>
                {entry && (
                  <details className="flow-drawer">
                    <summary>Share</summary>
                    <ShareChallengeButton
                      packId={p.packId}
                      packVersion={p.packVersion}
                      seed={p.seed}
                      graphJson={p.graphJson}
                      score={p.score}
                      label="Beat My Edit"
                    />
                  </details>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {state.drafts.length > 0 && (
        <section className="draft-section">
          <h2>Drafts</h2>
          <ul data-testid="draft-list">
            {state.drafts.map((d) => (
              <li key={d.id}>
                {PACK_REGISTRY[d.packId]?.title ?? "Draft"}
                <button type="button" onClick={() => dispatch({ type: "LOAD_DRAFT", draftId: d.id })}>
                  Load
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </FlowChrome>
  );
}
